import axios from 'axios';

// URL base del API
// - En desarrollo: usar `/api` para aprovechar el proxy de Vite (vite.config.js → localhost:5001)
// - En producción: apuntar a Azure
const API_URL = import.meta.env.DEV
  ? '/api'
  : 'https://nexai-billing-api.lmendoza-sv.workers.dev/api';

export const resolvePdfUrl = (url) => {
  if (!url || /^https?:\/\//i.test(url)) return url;
  return `${API_URL.replace(/\/api\/?$/, '')}${url.startsWith('/') ? url : `/${url}`}`;
};

// Función helper para obtener el token de autenticación 
const getAuthToken = () => {
  return localStorage.getItem('nx2026_token');
};

const getCurrentCompanyId = () => {
  try {
    const modulesData = localStorage.getItem('nx2026_modules');
    if (modulesData) {
      const modules = JSON.parse(modulesData);
      const billingModule = modules.find((m) =>
        m.modules && m.modules.some((mod) => mod.id && mod.id.toUpperCase().includes('BILLING'))
      );
      if (billingModule?.company?.id) {
        return billingModule.company.id;
      }
      if (modules[0]?.company?.id) {
        return modules[0].company.id;
      }
    }

    const companyData = localStorage.getItem('nx2026_company');
    if (companyData) {
      const company = JSON.parse(companyData);
      if (company?.id) {
        return company.id;
      }
    }
  } catch (error) {
    console.error('[API] Error getting current company id:', error);
  }

  return null;
};

const withCompanyId = (params = {}) => {
  const companyId = getCurrentCompanyId();
  if (!companyId || params.companyId || params.company_id) {
    return params;
  }
  return { ...params, companyId };
};


// Función helper para hacer fetch con autenticación
// Alternativa a fetch() que incluye el Bearer token automáticamente
export const authenticatedFetch = async (url, options = {}) => {
  const token = getAuthToken();
  
  // En producción, el frontend (fel.nexai.chat) NO hace proxy de `/api`.
  // Si se hace `fetch('/api/...')` termina respondiendo el `index.html` del SPA.
  // Por eso, cuando recibimos una ruta relativa `/api/...` reescribimos a `API_URL`.
  const resolvedUrl = (() => {
    if (typeof url !== 'string') return url;
    // URLs absolutas: no tocar
    if (/^https?:\/\//i.test(url)) return url;
    // Solo reescribir rutas del API (evita romper assets/rutas del SPA)
    if (url.startsWith('/api/')) {
      if (import.meta.env.DEV) return url; // Vite proxy en desarrollo
      return `${API_URL}${url.replace(/^\/api/, '')}`;
    }
    return url;
  })();

  const headers = {
    'Content-Type': 'application/json',
    ...options.headers,
  };
  
  if (token) {
    headers['Authorization'] = `Bearer ${token}`;
    console.log('[authenticatedFetch] Token enviado:', token.substring(0, 20) + '...');
  } else {
    console.warn('[authenticatedFetch] No hay token en localStorage!');
  }
  
  if (resolvedUrl !== url) {
    console.log('[authenticatedFetch] URL reescrita:', url, '=>', resolvedUrl);
  }

  const response = await fetch(resolvedUrl, {
    ...options,
    headers,
  });
  
  return response;
};

/**
 * Lee una respuesta del API sin asumir que el servidor siempre devuelve JSON.
 * Los fallbacks HTML del SPA/proxy empiezan con `<!doctype ...>` y antes hacían
 * fallar la pantalla con el poco útil "Unexpected token '<'".
 */
export const parseJsonResponse = async (response, fallbackMessage = 'Respuesta inválida del servidor') => {
  const contentType = response.headers.get('content-type') || '';
  const rawBody = await response.text();

  if (!rawBody.trim()) {
    const error = new Error(`${fallbackMessage}: respuesta vacía (HTTP ${response.status})`);
    error.status = response.status;
    throw error;
  }

  try {
    return JSON.parse(rawBody);
  } catch (cause) {
    const looksLikeHtml = contentType.toLowerCase().includes('text/html') || /^\s*</.test(rawBody);
    let route = '';
    try {
      route = new URL(response.url).pathname;
    } catch {
      route = response.url || '';
    }

    const detail = looksLikeHtml
      ? `el servidor devolvió HTML en lugar de JSON${route ? ` para ${route}` : ''}`
      : `el servidor devolvió un formato no JSON (${contentType || 'sin Content-Type'})`;
    const error = new Error(`${fallbackMessage}: ${detail} (HTTP ${response.status})`);
    error.status = response.status;
    error.contentType = contentType;
    error.cause = cause;
    throw error;
  }
};

// Crear instancia de axios
const api = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  // Algunos procesos (firmar/enviar a MH, búsquedas pesadas) pueden tardar más de 10s.
  timeout: 60000,
});

// Interceptor para agregar token JWT a todas las requests
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('nx2026_token');
    
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
      console.log('[API] Token enviado:', token.substring(0, 20) + '...');
    } else {
      console.warn('[API] No hay token en localStorage!');
    }
    
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Interceptor para manejar respuestas y errores
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;
    
    // Si el token expiró (401) y no es una solicitud de refresh
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      
      // Limpiar datos de autenticación
      localStorage.removeItem('nx2026_token');
      localStorage.removeItem('nx2026_user');
      
      // Redireccionar a login si no estamos ya ahí
      if (window.location.pathname !== '/login') {
        window.location.href = '/login';
      }
    }
    
    return Promise.reject(error);
  }
);

// ============================================
// AUTENTICACIÓN
// ============================================
export const authService = {
  // Login con email/password tradicional (fallback)
  login: async (email, password) => {
    const response = await api.post('/auth/login', { email, password });
    return response.data;
  },
  
  // Login con Firebase token (recomendado para email/password)
  loginWithFirebase: async (idToken) => {
    const response = await api.post('/auth/login', { idToken });
    return response.data;
  },
  
  // Login con Google OAuth
  loginGoogle: async (idToken) => {
    const response = await api.post('/auth/google', { idToken });
    return response.data;
  },
  
  // Registrar nuevo usuario con Firebase
  registerWithFirebase: async (idToken, nombre) => {
    const response = await api.post('/auth/register', { idToken, nombre });
    return response.data;
  },
  
  // Logout
  logout: async () => {
    try {
      await api.post('/auth/logout');
    } finally {
      localStorage.removeItem('nx2026_token');
      localStorage.removeItem('nx2026_user');
    }
  },
  
  // Obtener usuario actual
  getCurrentUser: async () => {
    const response = await api.get('/auth/me');
    return response.data;
  },
  
  // Verificar token
  verifyToken: async () => {
    const response = await api.get('/auth/verify');
    return response.data;
  },
};

// ============================================
// CAJAS
// ============================================
export const cajaService = {
  // Listar todas las cajas
  getAll: async () => {
    const response = await api.get('/cajas');
    return response.data;
  },
  
  // Obtener caja activa
  getActiva: async () => {
    const response = await api.get('/cajas/activa');
    return response.data;
  },
  
  // Obtener caja por ID
  getById: async (id) => {
    const response = await api.get(`/cajas/${id}`);
    return response.data;
  },
  
  // Obtener estado de caja
  getEstado: async (id) => {
    const response = await api.get(`/cajas/${id}/estado`);
    return response.data;
  },
  
  // Abrir caja
  abrir: async (dinero_inicial) => {
    const response = await api.post('/cajas/abrir', { dinero_inicial });
    return response.data;
  },
  
  // Cerrar caja
  cerrar: async (id, dinero_final, observaciones) => {
    const response = await api.post(`/cajas/${id}/cerrar`, { dinero_final, observaciones });
    return response.data;
  },
};

// ============================================
// TRANSACCIONES
// ============================================
export const transaccionService = {
  // Listar transacciones con filtros
  getAll: async (filtros = {}) => {
    const response = await api.get('/transacciones', { params: filtros });
    return response.data;
  },
  
  // Obtener transacción por ID
  getById: async (id) => {
    const response = await api.get(`/transacciones/${id}`);
    return response.data;
  },
  
  // Crear transacción
  create: async (transaccion) => {
    const response = await api.post('/transacciones', transaccion);
    return response.data;
  },
  
  // Actualizar transacción
  update: async (id, transaccion) => {
    const response = await api.put(`/transacciones/${id}`, transaccion);
    return response.data;
  },
  
  // Eliminar transacción
  delete: async (id) => {
    const response = await api.delete(`/transacciones/${id}`);
    return response.data;
  },
  
  // Generar reporte
  getReporte: async (filtros = {}) => {
    const response = await api.get('/transacciones/reporte', { params: filtros });
    return response.data;
  },
};

// ============================================
// CIERRES
// ============================================
export const cierreService = {
  // Listar cierres
  getAll: async (filtros = {}) => {
    const response = await api.get('/cierres', { params: filtros });
    return response.data;
  },
  
  // Obtener cierre por ID
  getById: async (id) => {
    const response = await api.get(`/cierres/${id}`);
    return response.data;
  },
  
  // Crear cierre
  create: async (cierre) => {
    const response = await api.post('/cierres', cierre);
    return response.data;
  },
  
  // Eliminar cierre
  delete: async (id) => {
    const response = await api.delete(`/cierres/${id}`);
    return response.data;
  },
};

// ============================================
// PRODUCTOS
// ============================================
export const productService = {
  // Listar productos
  getAll: async (filtros = {}) => {
    const response = await api.get('/products', { params: filtros });
    return response.data;
  },
  
  // Obtener producto por ID
  getById: async (id) => {
    const response = await api.get(`/products/${id}`);
    return response.data;
  },
  
  // Crear producto
  create: async (producto) => {
    const response = await api.post('/products', producto);
    return response.data;
  },
  
  // Actualizar producto
  update: async (id, producto) => {
    const response = await api.put(`/products/${id}`, producto);
    return response.data;
  },
  
  // Eliminar producto
  delete: async (id) => {
    const response = await api.delete(`/products/${id}`);
    return response.data;
  },
};

// ============================================
// VENTAS
// ============================================
export const saleService = {
  // Listar ventas
  getAll: async (filtros = {}) => {
    const response = await api.get('/sales', { params: filtros });
    return response.data;
  },
  
  // Obtener venta por ID
  getById: async (id) => {
    const response = await api.get(`/sales/${id}`);
    return response.data;
  },
  
  // Crear venta
  create: async (venta) => {
    const response = await api.post('/sales', venta);
    return response.data;
  },
  
  // Obtener reporte de ventas
  getReporte: async (filtros = {}) => {
    const response = await api.get('/sales/reporte', { params: filtros });
    return response.data;
  },
};

// ============================================
// FACTURAS
// ============================================
export const invoiceService = {
  // Obtener tipos de facturas
  getTipos: async () => {
    const response = await api.get('/invoices/tipos');
    return response.data;
  },
  
  // Obtener siguiente correlativo
  getSiguienteCorrelativo: async (tipo, params = {}) => {
    const response = await api.get(`/invoices/serie/${tipo}`, { params });
    return response.data;
  },
  
  // Listar facturas
  getAll: async (filtros = {}) => {
    const response = await api.get('/invoices', { params: filtros });
    return response.data;
  },
  
  // Obtener factura por ID (con PDF o JSON)
  getById: async (id) => {
    const response = await api.get(`/invoices/${id}`);
    return response.data;
  },
  
  // Generar PDF para una factura
  generatePdf: async (id) => {
    const response = await api.post(`/invoices/${id}/generate-pdf`);
    return response.data;
  },
  
  // Crear factura
  create: async (factura) => {
    const response = await api.post('/invoices', factura);
    return response.data;
  },
  
  // Actualizar factura
  update: async (id, factura) => {
    const response = await api.put(`/invoices/${id}`, factura);
    return response.data;
  },
  
  // Anular factura
  cancel: async (id) => {
    const response = await api.delete(`/invoices/${id}`);
    return response.data;
  },
  
  // Obtener reporte
  getReporte: async (filtros = {}) => {
    const response = await api.get('/invoices/reporte', { params: filtros });
    return response.data;
  },
};

// ============================================
// CLIENTES
// ============================================
export const clienteService = {
  // Listar clientes
  getAll: async (filtros = {}) => {
    const response = await api.get('/clientes', { params: filtros });
    return response.data;
  },
  
  // Obtener cliente por ID
  getById: async (id) => {
    const response = await api.get(`/clientes/${id}`);
    return response.data;
  },
  
  // Crear cliente
  create: async (cliente) => {
    const response = await api.post('/clientes', cliente);
    return response.data;
  },
  
  // Actualizar cliente
  update: async (id, cliente) => {
    const response = await api.put(`/clientes/${id}`, cliente);
    return response.data;
  },
  
  // Eliminar cliente
  delete: async (id) => {
    const response = await api.delete(`/clientes/${id}`);
    return response.data;
  },
};

// ============================================
// RECEPTOR (for DTE - searches tbl_customers)
// ============================================
export const receptorService = {
  // Buscar receptores por tipo y número de documento (búsqueda incremental)
  // El backend espera: tipoDocumento + query + opcionalmente companyId
  search: async (tipoDocumento, query, companyId = null) => {
    const params = new URLSearchParams({
      tipoDocumento,
      query
    });
    if (companyId) {
      params.append('companyId', companyId);
    }
    const response = await api.get('/clientes/receptores/search', {
      params
    });
    return response.data;
  },
  
  // Crear nuevo receptor
  create: async (receptor) => {
    const response = await api.post('/clientes/receptores', receptor);
    return response.data;
  },
  
  // Obtener receptor por ID
  getById: async (id) => {
    const response = await api.get(`/clientes/receptores/${id}`);
    return response.data;
  },
};

// ============================================
// PROVEEDORES
// ============================================
export const proveedorService = {
  // Listar proveedores
  getAll: async (filtros = {}) => {
    const response = await api.get('/proveedores', { params: filtros });
    return response.data;
  },
  
  // Obtener proveedor por ID
  getById: async (id) => {
    const response = await api.get(`/proveedores/${id}`);
    return response.data;
  },
  
  // Crear proveedor
  create: async (proveedor) => {
    const response = await api.post('/proveedores', proveedor);
    return response.data;
  },
  
  // Actualizar proveedor
  update: async (id, proveedor) => {
    const response = await api.put(`/proveedores/${id}`, proveedor);
    return response.data;
  },
  
  // Eliminar proveedor
  delete: async (id) => {
    const response = await api.delete(`/proveedores/${id}`);
    return response.data;
  },
};

// ============================================
// EMPLEADOS
// ============================================
export const empleadoService = {
  // Listar empleados
  getAll: async (filtros = {}) => {
    const response = await api.get('/empleados', { params: filtros });
    return response.data;
  },
  
  // Obtener empleado por ID
  getById: async (id) => {
    const response = await api.get(`/empleados/${id}`);
    return response.data;
  },
  
  // Crear empleado
  create: async (empleado) => {
    const response = await api.post('/empleados', empleado);
    return response.data;
  },
  
  // Actualizar empleado
  update: async (id, empleado) => {
    const response = await api.put(`/empleados/${id}`, empleado);
    return response.data;
  },
  
  // Eliminar empleado
  delete: async (id) => {
    const response = await api.delete(`/empleados/${id}`);
    return response.data;
  },
};

// ============================================
// SETTINGS
// ============================================
export const settingsService = {
  // Obtener catálogos
  getCatalogs: async () => {
    const response = await api.get('/settings/catalogs');
    return response.data;
  },
  
  // Obtener empresa por ID
  getCompany: async (companyId) => {
    const response = await api.get(`/settings/company/${companyId}`);
    return response.data;
  },
  
  // Actualizar empresa
  updateCompany: async (companyId, data) => {
    const response = await api.put(`/settings/company/${companyId}`, data);
    return response.data;
  },
  
  // Obtener logo
  getLogo: async (companyId) => {
    const response = await api.get(`/settings/company/${companyId}/logo`);
    return response.data;
  },
  
  // Subir logo - ahora acepta un objeto File directamente
  uploadLogo: async (companyId, file) => {
    // Crear FormData para enviar como multipart/form-data
    const formData = new FormData();
    formData.append('logo', file, file.name);
    
    const response = await api.post(`/settings/company/${companyId}/logo`, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  },
  
  // Obtener sucursales
  getBranches: async (companyId) => {
    const response = await api.get(`/settings/company/${companyId}/branches`);
    return response.data;
  },
  
  // Crear sucursal
  createBranch: async (companyId, data) => {
    const response = await api.post(`/settings/company/${companyId}/branches`, data);
    return response.data;
  },
  
  // Obtener sucursal por ID
  getBranch: async (branchId) => {
    const response = await api.get(`/settings/branches/${branchId}`);
    return response.data;
  },
  
  // Actualizar sucursal
  updateBranch: async (branchId, data) => {
    const response = await api.put(`/settings/branches/${branchId}`, data);
    return response.data;
  },
  
  // Eliminar sucursal
  deleteBranch: async (branchId) => {
    const response = await api.delete(`/settings/branches/${branchId}`);
    return response.data;
  },
  
  // Obtener puntos de venta/cajas
  getPOS: async (companyId) => {
    const response = await api.get(`/settings/company/${companyId}/pos`);
    return response.data;
  },
  
  // Crear punto de venta/caja
  createPOS: async (companyId, data) => {
    const response = await api.post(`/settings/company/${companyId}/pos`, data);
    return response.data;
  },
  
  // Obtener punto de venta por ID
  getPOSById: async (posId) => {
    const response = await api.get(`/settings/pos/${posId}`);
    return response.data;
  },
  
  // Actualizar punto de venta/caja
  updatePOS: async (posId, data) => {
    const response = await api.put(`/settings/pos/${posId}`, data);
    return response.data;
  },
  
  // Eliminar punto de venta/caja
  deletePOS: async (posId) => {
    const response = await api.delete(`/settings/pos/${posId}`);
    return response.data;
  },
};

// ============================================
// DTE (Documentos Tributarios Electrónicos)
// ============================================
export const dteService = {
  // Obtener documentos emitidos con paginación y filtros aplicados en el servidor
  getRecent: async ({ page = 1, pageSize = 25, fechaEmision = '', tipoDte = '' } = {}) => {
    const response = await api.get('/dte/recent', {
      params: {
        page,
        pageSize,
        ...(fechaEmision ? { fechaEmision } : {}),
        ...(tipoDte ? { tipoDte } : {}),
      },
    });
    return response.data;
  },

  // Obtener datos de la empresa (para anulación)
  getCompany: async (companyId) => {
    const response = await api.get(`/settings/company/${companyId}`);
    return response.data;
  },

  // Obtener datos completos de un documento para anulación
  getDocument: async (codigoGeneracion) => {
    const response = await api.get(`/dte/document/${codigoGeneracion}`);
    return response.data;
  },

  // Firmar y enviar DTE
  signAndSend: async (dteData) => {
    const response = await api.post('/dte/sign-and-send', dteData);
    return response.data;
  },

  // Firmar DTE
  sign: async (dteData) => {
    const response = await api.post('/dte/sign', dteData);
    return response.data;
  },

  // Enviar a MH
  sendToMH: async (dteData) => {
    const response = await api.post('/dte/send-to-mh', dteData);
    return response.data;
  },

  // Obtener estado del documento
  getStatus: async (documentId) => {
    const response = await api.get(`/dte/status/${documentId}`);
    return response.data;
  },

  // Obtener token firmado
  getSignedToken: async (documentId) => {
    const response = await api.get(`/dte/signed-token/${documentId}`);
    return response.data;
  },

  // Autenticación DTE
  auth: async (credentials) => {
    const response = await api.post('/dte/auth', credentials);
    return response.data;
  },

  // Obtener PDF
  getPdf: async (documentId) => {
    const response = await api.get(`/dte/pdf/${documentId}`);
    return response.data;
  },

  // Generar PDF
  generatePdf: async (documentId) => {
    const response = await api.post(`/dte/pdf/generate/${documentId}`);
    return response.data;
  },

  // Anular DTE
  anular: async (anulacionData) => {
    const response = await api.post('/dte/anular', anulacionData);
    return response.data;
  },

  // Enviar correo con PDF
  sendEmail: async (documentId, emailTo, emailName = null) => {
    const response = await api.post('/dte/send-email', { documentId, emailTo, emailName });
    return response.data;
  },
};

// ============================================
// TAX BOOKS / ANEXOS MH
// ============================================
export const taxBooksService = {
  getCatalog: async (catalogName, params = {}) => {
    const response = await api.get(`/tax-books/catalogs/${catalogName}`, { params: withCompanyId(params) });
    return response.data;
  },

  getPreview: async (path, params = {}) => {
    const response = await api.get(`/tax-books/${path}`, { params: withCompanyId(params) });
    return response.data;
  },

  exportCsv: async (path, params = {}) => {
    const response = await api.get(`/tax-books/${path}`, {
      params: withCompanyId(params),
      responseType: 'blob',
    });
    return response.data;
  },

  getPurchases: async (params = {}) => {
    const response = await api.get('/tax-books/purchases', { params: withCompanyId(params) });
    return response.data;
  },

  getPurchase: async (id) => {
    const response = await api.get(`/tax-books/purchases/${id}`, { params: withCompanyId() });
    return response.data;
  },

  createPurchase: async (payload) => {
    const response = await api.post('/tax-books/purchases', withCompanyId(payload));
    return response.data;
  },

  updatePurchase: async (id, payload) => {
    const response = await api.put(`/tax-books/purchases/${id}`, withCompanyId(payload));
    return response.data;
  },

  deletePurchase: async (id) => {
    const response = await api.delete(`/tax-books/purchases/${id}`, { data: withCompanyId() });
    return response.data;
  },
};

export default api;
